import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { BiSave, BiBullseye, BiLoaderCircle } from 'react-icons/bi';

import { API_BASE_URL } from '../utils/urls.js';


const formatRoleName = (roleName = "") => {
    return roleName.replace(/_/g, " ").split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};

const ServerTaskUpdate = () => {
    const { taskId } = useParams();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        task_title: '',
        task_description: '',
        start_time: '',
        end_time: '',
        case_id: '',
        user_id: null,
        status: 'pending'
    });

    const [location, setLocation] = useState({ latitude: null, longitude: null });
    const [geoStatus, setGeoStatus] = useState('Awaiting GPS capture...');

    const currentUser = JSON.parse(localStorage.getItem('user'));
    const currentUserId = currentUser?.id;
    const isFieldAgent = currentUser?.roleName === 'field_agent';
    
    const [isAssignedAgent, setIsAssignedAgent] = useState(false); 
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitStatus, setSubmitStatus] = useState('');
    
    const toLocalDatetime = (isoDate) => {
        if (!isoDate) return 'N/A';
        return new Date(isoDate).toLocaleString();
    };
    
    // --- Geolocation Function ---
    const captureLocation = () => {
        setGeoStatus('Acquiring high-accuracy GPS...');
        setLocation({ latitude: null, longitude: null }); // Clear old location on re-capture attempt
        
        if (!navigator.geolocation) {
            setGeoStatus('Geolocation not supported by device.');
            return;
        }

        const geoOptions = { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 };

        navigator.geolocation.getCurrentPosition((position) => {
            setLocation({
                latitude: position.coords.latitude.toFixed(6),
                longitude: position.coords.longitude.toFixed(6),
            });
            setGeoStatus('Location Captured (Ready for Update)');
        }, (error) => {
            setGeoStatus(`Location Error (${error.code}): ${error.message}. Please try again.`);
        }, geoOptions);
    };
    // ---------------------------

    // Initial Data Fetch and Assignment Check
    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem('token');
            const currentUserIdInt = parseInt(currentUserId);

            if (!token || !isFieldAgent || !taskId) {
                if (!isFieldAgent) setError('Access Denied: Only Field Agents can use this task update page.');
                else if (!token) setError('Authentication token not found.');
                
                setLoading(false);
                return;
            }
            
            try {
                // API Call is now guaranteed to run if all checks pass
                const taskRes = await axios.get(`${API_BASE_URL}/tasks/${taskId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const taskData = taskRes.data;
                
                const assignedUserId = parseInt(taskData.user_id);
                const isUserAssigned = assignedUserId === currentUserIdInt;

                if (!isUserAssigned) {
                    setIsAssignedAgent(false);
                    throw new Error("Access denied: Not your assigned task.");
                }
                
                // If assignment is confirmed:
                setIsAssignedAgent(true);
                setFormData({
                    task_title: taskData.task_title || '',
                    task_description: taskData.task_description || '',
                    start_time: taskData.start_time || '',
                    end_time: taskData.end_time || '',
                    case_id: taskData.case_id || '',
                    user_id: taskData.user_id,
                    status: taskData.status || 'pending',
                });
                
                // Start location capture immediately upon loading task data
                captureLocation();

            } catch (err) {
                console.error('Error fetching data:', err);
                setError(err.response?.data?.message || err.message || 'Failed to load task. Check assignment.');
            } finally {
                setLoading(false);
            }
        };

        if (taskId) fetchData();
    }, [taskId, currentUserId, isFieldAgent]); 

    const handleChange = (e) => {
        // Only allow status and description changes for the Field Agent
        const { name, value } = e.target;
        if (name === 'status' || name === 'task_description') {
            setFormData(prevData => ({ ...prevData, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus('submitting');
        const token = localStorage.getItem('token');
        
        if (!isAssignedAgent) {
             setSubmitStatus('error');
             setError('Not authorized to submit changes for this task.');
             return;
        }
        
        // Require location for any status change that indicates field work occurred
        const requiresLocation = formData.status === 'in-progress' || formData.status === 'completed';

        if (requiresLocation && !location.latitude) {
            setSubmitStatus('error');
            setError('GPS location must be captured to log this status. Please click "Re-capture Location"');
            return;
        }

        // Payload for Field Agent: ONLY status, description, case_id, and GPS data
        const payload = {
            status: formData.status,
            task_description: formData.task_description, 
            case_id: formData.case_id, 
            latitude: location.latitude,
            longitude: location.longitude, 
        };

        try {
            await axios.put(`${API_BASE_URL}/tasks/${taskId}`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSubmitStatus('success');
            setTimeout(() => navigate(`/tasks/user/${currentUserId}`), 1500); 
        } catch (err) {
            setSubmitStatus('error');
            setError(err.response?.data?.message || 'Submission failed. Please try again.');
        }
    };

    // Conditional Rendering Checks
    if (!isFieldAgent) {
        return <div className="p-6 text-center text-red-500">Access Denied. This route is for Field Agents only.</div>;
    }
    if (loading) return <div className="p-6 text-center flex items-center justify-center text-lg"><BiLoaderCircle className='animate-spin mr-2'/> Loading task details...</div>;
    if (error && submitStatus !== 'submitting') return <div className="p-6 text-center text-red-500">{error}</div>;

    const isCompletedPermanently = formData.status === 'completed'; 
    const isLocationReady = !!location.latitude;
    
    const statusColor = formData.status === 'completed' ? 'bg-green-100 text-green-800' : 
                        formData.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800';

    return (
        <div className="p-6 max-w-xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Task Execution</h1>

            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-2xl p-6 space-y-6 border-t-8 border-indigo-600">
                
                {/* Read-Only Task Details */}
                <div className="space-y-3 pb-4 border-b">
                    <p className="text-2xl font-bold text-gray-900">{formData.task_title}</p>
                    <div className='grid grid-cols-2 text-sm text-gray-600'>
                         <p>Due: {toLocalDatetime(formData.end_time)}</p>
                         <p>Start: {toLocalDatetime(formData.start_time)}</p>
                    </div>
                    {/* Note: This shows the initial description set by the manager */}
                    <p className="p-3 bg-gray-50 rounded-md whitespace-pre-wrap">{formData.task_description || "No description provided."}</p>
                </div>
                
                {/* --- Agent Action Block --- */}
                <div className='pt-4 space-y-4'>
                    <h2 className='text-xl font-semibold text-indigo-700'>Log Work & Update Status</h2>

                    {/* 1. Status Dropdown */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">New Status</label>
                        <select 
                            name="status" 
                            value={formData.status} 
                            onChange={handleChange} 
                            disabled={isCompletedPermanently}
                            className={`shadow-sm border border-gray-300 text-base rounded-lg focus:outline-none focus:ring-4 block w-full p-2.5 ${statusColor}`}
                        >
                            <option value="pending">Pending</option>
                            <option value="in-progress">In-Progress</option>
                            <option value="completed">Completed</option>
                            <option value="on-hold">On Hold</option>
                        </select>
                         {isCompletedPermanently && <p className="text-sm text-green-600 mt-1">Status saved as COMPLETE. Contact dispatch if further changes are required.</p>}
                    </div>

                    {/* 2. Agent Notes/Outcome */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Final Notes / Outcome</label>
                        <textarea 
                            name="task_description" 
                            rows="3" 
                            value={formData.task_description} 
                            onChange={handleChange} 
                            disabled={isCompletedPermanently}
                            className={`shadow-sm border border-gray-300 text-sm rounded-lg focus:outline-none focus:ring-4 block w-full p-2 mt-1 bg-yellow-50 focus:ring-yellow-200`}
                            placeholder="Add final notes or outcome details here..."
                        ></textarea>
                    </div>
                    
                    {/* 3. GPS Check-in Status */}
                    <div className="p-3 border rounded-lg bg-indigo-50 border-indigo-200">
                        <p className="text-sm font-bold text-indigo-700 flex justify-between items-center">
                            GPS Location Proof: 
                            {isLocationReady ? (
                                <span className='text-green-600 flex items-center'>
                                    <BiBullseye className='mr-1'/> Captured
                                </span>
                            ) : (
                                <span className='text-red-600 flex items-center'>
                                    <BiLoaderCircle className='mr-1 animate-spin'/> Capturing...
                                </span>
                            )}
                        </p>
                        <p className={`text-xs mt-1 ${isLocationReady ? 'text-gray-800' : 'text-gray-600'}`}>
                            {geoStatus}
                        </p>
                        {/* Display Lat/Long only when captured */}
                        {isLocationReady && (
                             <p className="text-xs font-semibold text-gray-800 mt-1 p-1 bg-indigo-100 rounded">
                                 Lat: {location.latitude}, Long: {location.longitude}
                             </p>
                        )}
                        
                        {/* Only allow re-capture if not captured OR if the task isn't permanently completed */}
                        {(!isLocationReady || !isCompletedPermanently) && (
                            <button 
                                type="button" 
                                onClick={captureLocation} 
                                disabled={submitStatus === 'submitting'}
                                className="mt-2 text-xs font-medium text-white bg-indigo-500 px-3 py-1 rounded-md hover:bg-indigo-600 disabled:opacity-70"
                            >
                                Re-capture Location
                            </button>
                        )}
                    </div>
                </div>

                {/* Submission & Error */}
                <div className="flex justify-end space-x-4 pt-4">
                    <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition duration-300">
                        Back
                    </button>
                    <button 
                        type="submit" 
                        // Disable if submitting, or if the task is completed, or if location is required but not ready
                        disabled={submitStatus === 'submitting' || isCompletedPermanently || (['in-progress', 'completed'].includes(formData.status) && !isLocationReady)} 
                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition duration-300 disabled:opacity-50"
                    >
                        {submitStatus === 'submitting' ? 'Saving...' : 'Save Status & Notes'}
                    </button>
                </div>
                {error && submitStatus === 'error' && <p className="mt-4 text-red-500 text-center">{error}</p>}
                {submitStatus === 'success' && <p className="mt-4 text-green-500 text-center">Task updated successfully! Redirecting...</p>}
            </form>
        </div>
    );
};

export default ServerTaskUpdate;